import { useEffect, useState, type ReactElement } from "react";
import { fetchDocuments } from "../api/client";
import type { Document } from "../api/schemas";
import { useAccessToken } from "../auth/useAccessToken";
import { ConfigurationRequired } from "../shell/ConfigurationRequired";
import { PageHeader } from "../shell/PageHeader";
import { readConfig } from "../config";
import { Badge, Callout, DataTable, EmptyState, Spinner, Stack, Surface } from "../ui-kit";
import type { BadgeTone, DataTableColumn } from "../ui-kit";
import { formatBytes, formatDateTime } from "./formatters";
import styles from "./DocumentsView.module.css";

/**
 * Reinforcement only (Badge always renders its own text, WCAG 1.4.1) — a
 * status this schema has not seen yet (the producer, ISSUE-326, is not built)
 * falls back to `neutral` rather than guessing a tone.
 */
const STATUS_TONE: Record<string, BadgeTone> = {
  done: "success",
  failed: "danger",
  indexing: "info",
};

const COLUMNS: readonly DataTableColumn<Document>[] = [
  {
    key: "documentId",
    header: "Document",
    format: "identifier",
    cell: (row) => row.documentId,
  },
  {
    key: "channel",
    header: "Channel",
    cell: (row) => <Badge tone={row.channel === "mobile" ? "info" : "neutral"}>{row.channel}</Badge>,
  },
  { key: "ownerRef", header: "Owner", format: "identifier", cell: (row) => row.ownerRef },
  {
    key: "status",
    header: "Status",
    cell: (row) => <Badge tone={STATUS_TONE[row.status] ?? "neutral"}>{row.status}</Badge>,
  },
  {
    key: "sizeBytes",
    header: "Size",
    align: "end",
    format: "numeric",
    cell: (row) => formatBytes(row.sizeBytes),
  },
  { key: "startedAt", header: "Started", cell: (row) => formatDateTime(row.startedAt) },
  { key: "updatedAt", header: "Updated", cell: (row) => formatDateTime(row.updatedAt) },
];

type DocumentsRequestState =
  | { status: "loading" }
  | { status: "success"; rows: readonly Document[] }
  | { status: "error" };

/**
 * The documents list (ISSUE-329 Deliverable D2). Fires one `GET /documents`
 * on route load via `fetchDocuments` — no automatic retry, no manual retry
 * control (design spec: `docs/design/list-view-request-states.md`).
 * `cancelled` guards against a stale request committing state after this
 * effect's owner unmounts or reruns for a new `getAccessToken` identity.
 *
 * Only the allowed field set (ADR-052 §5) is a column here at all — there is
 * no field to accidentally render, since the raw filename and the
 * classifier-tag fields never reach this module (`fieldDiscipline.test.ts`
 * guards that boundary repo-wide).
 */
export function DocumentsView(): ReactElement {
  const config = readConfig();
  const getAccessToken = useAccessToken();
  const [state, setState] = useState<DocumentsRequestState>({ status: "loading" });

  useEffect(() => {
    if (!config.ok) {
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    fetchDocuments(getAccessToken).then(
      (response) => {
        if (!cancelled) {
          setState({ status: "success", rows: response.documents });
        }
      },
      () => {
        if (!cancelled) {
          setState({ status: "error" });
        }
      },
    );
    return (): void => {
      cancelled = true;
    };
  }, [config.ok, getAccessToken]);

  return (
    <Stack gap={6}>
      <PageHeader
        title="Documents"
        description="Processing metadata for every uploaded document. File names and classifier tags are
          never sent to this portal and are never shown."
      />
      <Surface as="section" aria-label="Documents" padding={config.ok ? "none" : "default"}>
        {!config.ok ? (
          <ConfigurationRequired missing={config.missing} />
        ) : state.status === "loading" ? (
          <div className={styles.stateRegion}>
            <Spinner label="Loading documents…" showLabel />
          </div>
        ) : state.status === "error" ? (
          <div className={styles.stateRegion}>
            <Callout tone="danger" role="alert" title="Documents could not be loaded">
              Document data could not be loaded. No data is being shown.
            </Callout>
          </div>
        ) : (
          <DataTable
            caption="Documents across all channels"
            captionHidden
            columns={COLUMNS}
            rows={state.rows}
            rowKey={(row) => row.documentId}
            empty={
              <EmptyState
                title="No documents yet"
                description="No documents have been uploaded on either channel yet."
              />
            }
          />
        )}
      </Surface>
    </Stack>
  );
}
